import { useEffect, useState } from "react";
import {
  Calendar as CalendarIcon,
  RefreshCw,
  Download,
  Image as ImageIcon,
  Clock,
  ChevronDown,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const PRESETS = [
  { key: "1h", label: "1h", hours: 1 },
  { key: "6h", label: "6h", hours: 6 },
  { key: "24h", label: "24h", hours: 24 },
  { key: "7d", label: "7d", hours: 24 * 7 },
  { key: "30d", label: "30d", hours: 24 * 30 },
];

/** Build a {from, to} ISO range from a preset key. */
export function rangeFromPreset(presetKey) {
  const p = PRESETS.find((x) => x.key === presetKey);
  if (!p) return null;
  const to = new Date();
  const from = new Date(to.getTime() - p.hours * 3600 * 1000);
  return { from: from.toISOString(), to: to.toISOString(), preset: p.key };
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  // datetime-local needs "YYYY-MM-DDTHH:mm" in local time
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function TimeRangeBar({
  range,
  onChange,
  onRefreshAll,
  onExportCsv,
  onExportPng,
  refreshing = false,
}) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(toLocalInputValue(range?.from));
  const [draftTo, setDraftTo] = useState(toLocalInputValue(range?.to));

  // keep draft values in sync when range is updated externally (preset change)
  useEffect(() => {
    setDraftFrom(toLocalInputValue(range?.from));
    setDraftTo(toLocalInputValue(range?.to));
  }, [range?.from, range?.to]);

  function applyPreset(key) {
    const r = rangeFromPreset(key);
    if (r) onChange?.(r);
  }

  function applyCustom() {
    const fromIso = localInputToIso(draftFrom);
    const toIso = localInputToIso(draftTo);
    if (!fromIso || !toIso) return;
    if (new Date(fromIso) >= new Date(toIso)) return;
    onChange?.({ from: fromIso, to: toIso, preset: "custom" });
    setOpen(false);
  }

  const activePreset = range?.preset;

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-2 py-2 shadow-sm"
      data-testid="time-range-bar"
    >
      <div className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Rango</span>
      </div>

      <div className="flex items-center gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => applyPreset(p.key)}
            data-testid={`time-range-preset-${p.key}`}
            className={`h-8 px-2.5 rounded-md text-xs font-medium transition-colors ${
              activePreset === p.key
                ? "bg-primary text-primary-foreground"
                : "text-foreground/70 hover:bg-accent hover:text-foreground"
            }`}
          >
            {p.label}
          </button>
        ))}

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="time-range-custom-trigger"
              className={`h-8 px-2.5 rounded-md text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                activePreset === "custom"
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/70 hover:bg-accent hover:text-foreground"
              }`}
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              Custom
              <ChevronDown className="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-3">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="tr-from" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Desde
                </Label>
                <Input
                  id="tr-from"
                  type="datetime-local"
                  value={draftFrom}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  data-testid="time-range-from"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tr-to" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Hasta
                </Label>
                <Input
                  id="tr-to"
                  type="datetime-local"
                  value={draftTo}
                  onChange={(e) => setDraftTo(e.target.value)}
                  data-testid="time-range-to"
                />
              </div>
              <Button
                size="sm"
                className="w-full"
                onClick={applyCustom}
                data-testid="time-range-apply"
              >
                Aplicar rango
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {onRefreshAll && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefreshAll}
            disabled={refreshing}
            data-testid="time-range-refresh-all"
            className="h-8"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            <span className="ml-1.5 hidden sm:inline">Refrescar todo</span>
          </Button>
        )}
        {(onExportCsv || onExportPng) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                data-testid="time-range-export"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="ml-1.5 hidden sm:inline">Exportar</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onExportCsv && (
                <DropdownMenuItem onClick={onExportCsv} data-testid="time-range-export-csv">
                  <Download className="h-4 w-4 mr-2" /> CSV (rango actual)
                </DropdownMenuItem>
              )}
              {onExportPng && (
                <DropdownMenuItem onClick={onExportPng} data-testid="time-range-export-png">
                  <ImageIcon className="h-4 w-4 mr-2" /> PNG (rango actual)
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
